import { useEffect, useMemo, useState } from "react";
import { CODE_TABS, STEPS, tokenize } from "./codeSamples";
import { useInView, Reveal, Eyebrow } from "./Reveal";

const REDUCED = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function CodeLine({ text, lang, n, active, cursor }) {
  const toks = useMemo(() => tokenize(text, lang), [text, lang]);
  return (
    <div className={`code-line ${active ? "code-line-active" : ""}`}>
      <span className="code-ln">{n}</span>
      <span className="whitespace-pre">
        {toks.map((t, i) => <span key={i} className={`tk-${t.t}`}>{t.v}</span>)}
        {cursor && <span className="code-cursor" />}
      </span>
    </div>
  );
}

function useTypewriter(code, tabKey, run) {
  const [n, setN] = useState(REDUCED ? code.length : 0);
  useEffect(() => {
    if (REDUCED) { setN(code.length); return; }
    setN(0);
    if (!run) return;
    const id = setInterval(() => setN((v) => {
      if (v >= code.length) { clearInterval(id); return v; }
      return Math.min(code.length, v + 3);
    }), 22);
    return () => clearInterval(id);
  }, [tabKey, run, code]);
  return n;
}

function Editor({ tab, typed }) {
  const lines = tab.code.slice(0, typed).split("\n");
  return (
    <div className="code-body" data-testid="code-editor-body">
      {lines.map((l, i) => (
        <CodeLine key={i} n={i + 1} text={l} lang={tab.lang} active={i === lines.length - 1}
          cursor={i === lines.length - 1} />
      ))}
    </div>
  );
}

function Preview({ tab, stage }) {
  return (
    <div className="flex flex-col h-full" data-testid="code-preview-panel">
      <div className="flex items-center justify-between px-4 h-11 border-b border-white/5">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#F4FFF9]/50">Interactive Preview — Illustrative</span>
        <span className={`h-2 w-2 rounded-full ${stage >= STEPS.length ? "bg-[#67F2B3] shadow-[0_0_10px_#67F2B3]" : "bg-[#67F2B3]/40 animate-pulse"}`} />
      </div>
      <div className="px-4 pt-4 grid grid-cols-5 gap-1.5" data-testid="code-preview-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-col gap-1.5">
            <div className={`h-1 rounded-full transition-colors duration-500 ${i < stage ? "bg-[#67F2B3]" : "bg-white/10"}`} />
            <span className={`font-mono text-[9px] sm:text-[10px] uppercase tracking-wider transition-colors duration-500 ${i < stage ? "text-[#67F2B3]" : "text-[#F4FFF9]/30"}`}>{s}</span>
          </div>
        ))}
      </div>
      <div className="flex-1 px-4 py-4 font-mono text-[11px] sm:text-xs leading-6 min-h-[170px]" data-testid="code-preview-terminal">
        {tab.output.slice(0, stage).map((l, i) => (
          <div key={`${tab.id}${i}`} className={`term-line ${i === 0 ? "text-[#F4FFF9]/50" : "text-[#F4FFF9]/80"}`}>
            {i > 0 && <span className="text-[#67F2B3]">✓ </span>}{l}
          </div>
        ))}
        {stage >= STEPS.length && <div className="term-line text-[#67F2B3] mt-1">● ready <span className="text-[#F4FFF9]/35">— preview only, nothing is executed</span></div>}
      </div>
    </div>
  );
}

export function CodeExperience() {
  const [ref, inView] = useInView({ threshold: 0.25 });
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [stage, setStage] = useState(REDUCED ? STEPS.length : 0);
  const tab = CODE_TABS[idx];
  const typed = useTypewriter(tab.code, idx, inView);
  const done = typed >= tab.code.length;

  useEffect(() => {
    if (REDUCED) { setStage(STEPS.length); return; }
    setStage(0);
  }, [idx]);
  useEffect(() => {
    if (!done || REDUCED) return;
    if (stage < STEPS.length) { const t = setTimeout(() => setStage((s) => s + 1), 520); return () => clearTimeout(t); }
    if (!paused) { const t = setTimeout(() => setIdx((i) => (i + 1) % CODE_TABS.length), 4200); return () => clearTimeout(t); }
  }, [done, stage, paused]);

  const pick = (i) => { setPaused(true); setIdx(i); };

  return (
    <section className="relative max-w-6xl mx-auto px-5 py-24 md:py-36" data-testid="code-experience-section">
      <Reveal className="max-w-2xl">
        <Eyebrow>Under the surface</Eyebrow>
        <h2 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-5">
          Built with logic.<br /><span className="text-[#F4FFF9]/45">Designed for continuity.</span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#F4FFF9]/60 max-w-xl leading-relaxed">
          Structured technology, managed workflows, and connected digital services — working together behind the scenes.
        </p>
      </Reveal>

      <div ref={ref} className={`rv ${inView ? "rv-in" : ""} mt-14 relative`}>
        <div className="absolute -inset-x-10 -inset-y-10 -z-10 bg-[#67F2B3]/[0.06] blur-[90px] rounded-full" />
        <div className="code-shell grid lg:grid-cols-[1.45fr_1fr]">
          <div className="min-w-0 border-b lg:border-b-0 lg:border-r border-white/5">
            <div className="flex items-center gap-3 px-4 h-11 border-b border-white/5">
              <div className="flex gap-1.5"><span className="win-dot" /><span className="win-dot" /><span className="win-dot" /></div>
              <span className="font-mono text-xs text-[#F4FFF9]/45 truncate" data-testid="code-file-name">{tab.file}</span>
            </div>
            <div role="tablist" className="flex gap-1 px-3 pt-3 overflow-x-auto no-scrollbar" data-testid="code-tabs">
              {CODE_TABS.map((t, i) => (
                <button key={t.id} role="tab" aria-selected={i === idx} onClick={() => pick(i)} data-testid={`code-tab-${t.id}`}
                  className={`code-tab ${i === idx ? "code-tab-active" : ""}`}>{t.label}</button>
              ))}
            </div>
            <div className="overflow-x-auto" onPointerDown={() => setPaused(true)}>
              <Editor tab={tab} typed={typed} />
            </div>
          </div>
          <Preview tab={tab} stage={done ? stage : 0} />
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4FFF9]/30">Design illustration · Sample code is fictional and does not run</p>
      </div>
    </section>
  );
}
